import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { FileViewerService } from '../../services/file-viewer.service';

const PHOTO_FIELD_TYPES = ['CAMERA_ONLY_PHOTO', 'GALLERY_ONLY_PHOTO', 'CAMERA_OR_GALLERY_PHOTO'];

const FIELD_TYPE_KEYS: Record<string, string> = {
	TOGGLE: 'requiredDriverInputSectionView.inputTypeToggle',
	NUMERIC: 'requiredDriverInputSectionView.inputTypeNumeric',
	TEXT: 'requiredDriverInputSectionView.inputTypeText',
	CAMERA_ONLY_PHOTO: 'requiredDriverInputSectionView.inputTypeCameraOnly',
	GALLERY_ONLY_PHOTO: 'requiredDriverInputSectionView.inputTypeGalleryOnly',
	CAMERA_OR_GALLERY_PHOTO: 'requiredDriverInputSectionView.inputTypeCameraOrGallery',
};

const EVENT_TYPE_KEYS_ONDEMAND: Record<string, string> = {
	PICKUP_OR_ARRIVAL: 'requiredDriverInputSectionView.eventTypeOnDemandPickup',
	DELIVERY_OR_COMPLETION: 'requiredDriverInputSectionView.eventTypeOnDemandDelivery',
};

const EVENT_TYPE_KEYS_ROUTED: Record<string, string> = {
	PICKUP_OR_ARRIVAL: 'requiredDriverInputSectionView.eventTypeRoutedArrival',
	DELIVERY_OR_COMPLETION: 'requiredDriverInputSectionView.eventTypeRoutedCompletion',
};

@Component({
	selector: 'app-required-driver-input-section',
	templateUrl: './required-driver-input-section.component.html',
	styleUrls: ['./required-driver-input-section.component.scss']
})
export class RequiredDriverInputSectionComponent implements OnChanges {
	@Input() public rdis: any[];
	@Input() public showStopType = false;
	@Input() public attachments: any[] = [];
	@Input() public access = 1;

	public visibleRdis: any[] = [];
	public displayedColumns: string[] = [];
	public hasAnyPhotoRdi = false;
	public photoAttachments: any[] = [];
	public isOpening$: Observable<boolean>;
	public fileExtensionRegex = /(?:\.([^.]+))?$/;

	constructor(private _translateService: TranslateService,
				private _fileViewerService: FileViewerService) {
		this.isOpening$ = this._fileViewerService.isOpening$;
	}

	// Inputs are always bound, so ngOnChanges fires on first render and is the single entry point
	// (an unbound usage would render nothing via *ngIf="visibleRdis.length > 0" anyway).
	ngOnChanges(changes: SimpleChanges): void {
		if (changes.rdis || changes.attachments || changes.showStopType) {
			this.recompute();
		}
	}

	openFile(file: any): void {
		this._fileViewerService.openFile(file);
	}

	// rdi.value for a photo RDI is a JSON list of attachment IDs (e.g. "[123,456]").
	private parsePhotoIds(value: any): string[] {
		if (!value || typeof value !== 'string') {
			return [];
		}
		try {
			const parsed = JSON.parse(value);
			return Array.isArray(parsed) ? parsed.map(id => String(id)) : [];
		} catch {
			return [];
		}
	}

	private recompute(): void {
		const eventMap = this.showStopType ? EVENT_TYPE_KEYS_ROUTED : EVENT_TYPE_KEYS_ONDEMAND;
		const allStopTypesLabel = this._translateService.instant('requiredDriverInputSectionView.allStopTypes');

		// Photo RDI attachments, with the file extension resolved (null-safe) for the download button.
		this.photoAttachments = (this.attachments ?? [])
			.filter(a => a?.fileCategory === 'PHOTO')
			.map(a => ({ ...a, _fileType: this.fileExtensionRegex.exec(a?.fileName ?? '')?.[1] ?? '' }));
		const photoAttachmentIds = new Set(this.photoAttachments.map(a => String(a?.id)));

		// The MT returns only the Show RDIs, so this maps them for display (no client-side hide/show filter).
		this.visibleRdis = (this.rdis ?? []).map(rdi => {
			const eventKey = eventMap[rdi?.eventType];
			const inputKey = FIELD_TYPE_KEYS[rdi?.fieldType];
			const isPhoto = PHOTO_FIELD_TYPES.includes(rdi?.fieldType);
			let stopTypeLabel: string;
			if (rdi?.stopType === null || rdi?.stopType === undefined || rdi?.stopType === '') {
				stopTypeLabel = allStopTypesLabel;
			} else {
				stopTypeLabel = String(rdi.stopType);
			}
			// True only when this photo RDI's own submitted attachment IDs are among the attachments passed
			// to this view — so a pending photo, or the print receipt (no attachments), shows "—".
			const hasPhotoAttachment = isPhoto
				&& this.parsePhotoIds(rdi?.value).some(id => photoAttachmentIds.has(id));
			return {
				...rdi,
				_stopTypeLabel: stopTypeLabel,
				_eventLabel: eventKey ? this._translateService.instant(eventKey) : (rdi?.eventType ?? ''),
				_inputTypeLabel: inputKey ? this._translateService.instant(inputKey) : (rdi?.fieldType ?? ''),
				_isPhoto: isPhoto,
				_hasPhotoAttachment: hasPhotoAttachment,
			};
		});

		this.hasAnyPhotoRdi = this.visibleRdis.some(r => r._isPhoto);

		const cols = ['prompt'];
		if (this.showStopType) {
			cols.push('stopType');
		}
		cols.push('event', 'inputType', 'driverResponse', 'completed');
		this.displayedColumns = cols;
	}
}
